import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { QuotationActions } from "./QuotationActions";
import { QuotationStatusBadge } from "./QuotationStatusBadge";
import { QuotationAmount } from "./QuotationAmount";
import { QuotationsPagination } from "./QuotationsPagination";
import { MobileQuotationCard } from "./MobileQuotationCard";
import { useIsMobile } from "@/hooks/use-mobile";

interface QuotationsDataTableProps {
  quotations: any[];
  onEdit: (quotation: any) => void;
  onDelete?: (quotation: any) => void;
  onView?: (quotation: any) => void;
  onShare?: (quotation: any) => void;
}

export function QuotationsDataTable({ 
  quotations, 
  onEdit, 
  onDelete,
  onView,
  onShare
}: QuotationsDataTableProps) {
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 10;
  const isMobile = useIsMobile();
  
  // Add empty state for when there are no quotations
  if (!quotations || quotations.length === 0) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-400 mb-4">Nenhum orçamento encontrado</p>
      </div>
    );
  }
  
  // Ensure quotations is an array
  const safeQuotations = Array.isArray(quotations) ? quotations : [];
  
  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentQuotations = safeQuotations.slice(indexOfFirstItem, indexOfLastItem);

  // For mobile view, render cards instead of a table
  if (isMobile) {
    return (
      <div className="space-y-4">
        <div className="space-y-3">
          {currentQuotations.map((quotation) => (
            <MobileQuotationCard
              key={quotation.id}
              quotation={quotation}
              onEdit={onEdit}
              onDelete={onDelete}
              onView={onView}
              onShare={onShare}
            />
          ))}
        </div>
        
        <QuotationsPagination
          totalItems={safeQuotations.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    );
  }

  // For desktop view, render the table
  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-gray-700">
              <TableHead className="text-white">Código</TableHead>
              <TableHead className="text-white">Cliente</TableHead>
              <TableHead className="text-white">Descrição</TableHead>
              <TableHead className="text-white">Valor Total</TableHead>
              <TableHead className="text-white">Status</TableHead>
              <TableHead className="text-white w-[200px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentQuotations.map((quotation) => (
              <TableRow 
                key={quotation.id}
                className="border-b border-gray-700 hover:bg-[#2A2F3C]"
              >
                <TableCell className="font-medium text-white">{quotation.code}</TableCell>
                <TableCell className="text-white">{quotation.customer?.name || "Cliente não especificado"}</TableCell>
                <TableCell className="text-white">{quotation.description}</TableCell>
                <TableCell>
                  <QuotationAmount amount={quotation.total_amount} />
                </TableCell>
                <TableCell>
                  <QuotationStatusBadge status={quotation.status} />
                </TableCell>
                <TableCell>
                  <QuotationActions 
                    quotation={quotation} 
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onView={onView}
                    onShare={onShare}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      <QuotationsPagination
        totalItems={safeQuotations.length}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}